/** Swap a stale bundle for the current build, in place.
 *
 * ⛔ 2026-10-15: hours after a deploy, 9% of Parikara page loads still booted the
 * previous bundle from `/sdk/v1/companion.js` — cached somewhere we do not
 * control, the same failure as the 2026-08-27 v1 pin. The operator's embed is
 * fixed, so the build is controlled from OUR side: every /suggestions response
 * names the current build (`X-SDK-Current`), and a bundle that is not it tears
 * itself down and loads `companion.js?b=<build>` — a URL no cache has seen.
 *
 * Not a page reload: the visitor's page, slip and route are untouched.
 * `destroy()`, never `hide()` — hide is the USER's decision (see retireWidget).
 * One attempt per target per PAGE (a window flag the swapped-in bundle shares):
 * if even the fresh URL came back stale, retrying would loop. Not per tab
 * session — the next reload may hit the stale cache again and must swap again. */
type SwapWindow = { __bcSwapTo?: string };

export function swapBundle(target: string, teardown: () => void,
  doc: Document = document, win: SwapWindow = window as unknown as SwapWindow): boolean {
  if (!/^[0-9a-f]{6,40}$/.test(target)) return false;
  const me = doc.querySelector("script[data-api-key]") as HTMLScriptElement | null;
  if (!me) return false;
  let src: string;
  try {
    const url = new URL("/sdk/v1/companion.js", me.src || doc.baseURI);
    url.searchParams.set("b", target);
    src = url.href;
  } catch { return false; }
  if (win.__bcSwapTo === target) return false;
  win.__bcSwapTo = target;
  teardown();
  const next = doc.createElement("script");
  next.src = src;
  for (const attr of Array.from(me.attributes)) {
    if (attr.name.startsWith("data-")) next.setAttribute(attr.name, attr.value);
  }
  // A nonce-based CSP runs only tags carrying the nonce; browsers hide the
  // attribute after load, so read the property. Never `integrity`: the new
  // file has a different hash by definition.
  const nonce = me.nonce || me.getAttribute("nonce");
  if (nonce) next.nonce = nonce;
  for (const name of ["crossorigin", "referrerpolicy"]) {
    const value = me.getAttribute(name);
    if (value !== null) next.setAttribute(name, value);
  }
  // The new bundle reads the FIRST script[data-api-key]; leave only its own.
  me.remove();
  (doc.head || doc.documentElement).appendChild(next);
  return true;
}
